import React, { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';
import './VillageMapLayer.css';

const VillageMapLayer = ({ 
  villages, 
  mode = 'markers', // 'markers' or 'heatmap'
  onVillageClick,
  highlightedVillage = null,
  categoryColors 
}) => {
  const map = useMap();
  const markersLayerRef = useRef(null);
  const heatmapLayerRef = useRef(null);
  const [currentZoom, setCurrentZoom] = useState(map.getZoom());

  // Track zoom level
  useEffect(() => {
    const handleZoom = () => {
      setCurrentZoom(map.getZoom());
    };
    
    map.on('zoomend', handleZoom);
    return () => {
      map.off('zoomend', handleZoom);
    };
  }, [map]);

  // Helper function to get color based on water level
  const getColorByWaterLevel = (waterLevel) => {
    if (waterLevel <= 2) return '#1fc21f';      // Excellent
    if (waterLevel <= 5) return '#7ed321';      // Good
    if (waterLevel <= 10) return '#f9c802';     // Moderate
    if (waterLevel <= 15) return '#ff8d1a';     // Critical
    return '#ea2328';                            // Severe
  };

  // Helper function to get intensity for heatmap (inverse of water level)
  const getIntensity = (waterLevel) => {
    // Higher water level = more stress = higher intensity
    return Math.min(waterLevel / 20, 1); // Normalize to 0-1
  };

  // Create/Update Markers
  useEffect(() => {
    if (mode === 'heatmap') return; // Skip if heatmap only

    // Clear existing markers
    if (markersLayerRef.current) {
      map.removeLayer(markersLayerRef.current);
    }

    if (!villages || villages.length === 0) return;

    // Create layer group for markers
    const markersLayer = L.layerGroup();

    villages.forEach(village => {
      const color = getColorByWaterLevel(village.waterLevel);
      const isHighlighted = highlightedVillage && 
        highlightedVillage.village === village.village &&
        highlightedVillage.block === village.block;
      
      // Determine marker size based on zoom and highlight
      const baseSize = currentZoom >= 10 ? 10 : 8;
      const size = isHighlighted ? baseSize + 4 : baseSize;
      
      // Create pulsing effect for critical villages
      const isPulsing = village.waterLevel > 10;
      
      // Create custom icon
      const icon = L.divIcon({
        className: 'village-map-marker',
        html: `
          <div class="village-marker-wrapper ${isHighlighted ? 'highlighted' : ''} ${isPulsing ? 'pulsing' : ''}">
            <div class="village-marker-dot" style="
              background-color: ${color};
              width: ${size}px;
              height: ${size}px;
              border: ${isHighlighted ? '3px' : '2px'} solid white;
              box-shadow: 0 2px ${isHighlighted ? '8px' : '4px'} rgba(0,0,0,${isHighlighted ? '0.4' : '0.3'});
            "></div>
            ${isHighlighted ? `<div class="village-marker-label">${village.village}</div>` : ''}
          </div>
        `,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
      });

      const marker = L.marker([village.latitude, village.longitude], { icon });
      
      // Tooltip
      marker.bindTooltip(`
        <div class="village-map-tooltip">
          <strong>${village.village}</strong><br/>
          <span style="color: ${color}; font-weight: bold;">${village.waterLevel} mbgl</span>
          <span style="color: #666;"> • ${village.block}</span>
        </div>
      `, {
        direction: 'top',
        offset: [0, -5],
        className: 'village-tooltip'
      });
      
      // Click handler
      marker.on('click', () => {
        if (onVillageClick) {
          onVillageClick(village);
        }
      });
      
      markersLayer.addLayer(marker);
    });

    // Add to map
    markersLayer.addTo(map);
    markersLayerRef.current = markersLayer;

    // Cleanup
    return () => {
      if (markersLayerRef.current) {
        map.removeLayer(markersLayerRef.current);
      }
    };
  }, [villages, mode, map, highlightedVillage, currentZoom, onVillageClick]);

  // Create/Update Heatmap
  useEffect(() => {
    if (mode === 'markers') return; // Skip if markers only

    // Clear existing heatmap
    if (heatmapLayerRef.current) {
      map.removeLayer(heatmapLayerRef.current);
    }

    if (!villages || villages.length === 0) return;

    // Prepare heatmap data: [lat, lng, intensity]
    const heatmapData = villages.map(village => [
      village.latitude,
      village.longitude,
      getIntensity(village.waterLevel)
    ]);

    // Create heatmap layer
    const heatmap = L.heatLayer(heatmapData, {
      radius: 25,
      blur: 15,
      maxZoom: 12,
      max: 1.0,
      gradient: {
        0.0: '#1fc21f',  // Green (low stress)
        0.3: '#7ed321',
        0.5: '#f9c802',  // Yellow (moderate)
        0.7: '#ff8d1a',  // Orange (high)
        1.0: '#ea2328'   // Red (critical)
      }
    });

    heatmap.addTo(map);
    heatmapLayerRef.current = heatmap;

    // Cleanup
    return () => {
      if (heatmapLayerRef.current) {
        map.removeLayer(heatmapLayerRef.current);
      }
    };
  }, [villages, mode, map]);

  return null;
};

export default VillageMapLayer;
